import { isPopulated } from '@gameday/common/functions'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Popover from '@material-ui/core/Popover'
import { withStyles } from '@material-ui/core/styles'
import cn from 'classnames'
import moment from 'moment'
import { useEffect, useState } from 'react'

import FieldText from '../Typography/FieldText'

const styles = theme => ({
  paper: {
    display: 'flex',
    alignItems: 'stretch',
    height: 200,
    padding: 6,
    '& $list:not(:last-child)': {
      marginRight: 6,
    },
  },
  list: {
    overflowY: 'scroll',
    minWidth: 60,
    '-ms-overflow-style': 'none',
    scrollbarWidth: 'none',
    '& $selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.colors.white,
      borderRadius: theme.borders.borderRadius,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        opacity: 0.9,
        color: theme.colors.white,
      },
    },
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: theme.typography.fontSizeSm,
    '&:hover': {
      backgroundColor: '#e6f5fc',
      borderRadius: theme.borders.borderRadius,
      color: theme.palette.primary.main,
    },
  },
  fieldText: {
    minWidth: 120,
  },
  selected: {},
})

const TimePicker = ({ classes, className, defaultValue, disabled, id, increment, label, value, onChange, ...rest }) => {
  const [hour, setHour] = useState('')
  const [minute, setMinute] = useState('')
  const [period, setPeriod] = useState('')
  const [anchorEl, setAnchorEl] = useState(null)
  const [timeDisplay, setTimeDisplay] = useState('--:-- --')

  useEffect(() => {
    // biome-ignore lint/correctness/noUnusedVariables: TODO
    const timeValue = moment(defaultValue, moment.HTML5_FMT.TIME).format('hh:mm a')

    if (isPopulated(value) && defaultValue !== timeDisplay) {
      const { convertedHour, convertedMinute, convertedPeriod } = getTimeParts(defaultValue)

      setHour(convertedHour)
      setMinute(convertedMinute)
      setPeriod(convertedPeriod)
      setTimeDisplay(defaultValue)
    }
  })

  const getTimeParts = (time = '') => {
    if (String(time).match(/^\d{2}:\d{2}$/)) {
      const convertedHour = moment(time, moment.HTML5_FMT.TIME).format('hh')
      const convertedMinute = moment(time, moment.HTML5_FMT.TIME).format('mm')
      const convertedPeriod = moment(time, moment.HTML5_FMT.TIME).format('a')

      return { convertedHour, convertedMinute, convertedPeriod }
    }

    if (String(time).match(/^\d{2}:\d{2} (am|pm)$/)) {
      const [timeValue, amPm] = time.split(' ')
      // biome-ignore lint/correctness/noUnsafeOptionalChaining: TODO
      const [convertedHour, convertedMinute] = timeValue?.split(':')
      const convertedPeriod = amPm && ['am', 'pm'].includes(amPm) ? amPm : 'am'

      return { convertedHour, convertedMinute, convertedPeriod }
    }

    return { convertedHour: '', convertedMinute: '', convertedPeriod: '' }
  }

  const handleClick = e => {
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (h, m, p) => {
    let hr = h
    if (p === 'pm' && h !== '12') {
      hr = parseInt(hr, 10)
      hr += 12
    } else if (p === 'am' && h === '12') {
      hr = '00'
    }
    setHour(h)
    setMinute(m)
    setPeriod(p)
    onChange(`${hr}:${m}`)
  }

  const open = Boolean(anchorEl)
  const popId = open ? 'simple-popover' : undefined

  const hours = []
  const minutes = []
  const getValue = item => (item > 9 ? `${item}` : `0${item}`)

  for (let i = 1; i <= 12; i += 1) {
    hours.push(getValue(i))
  }

  for (let i = 0; i <= 59; i += increment) {
    minutes.push(getValue(i))
  }

  const menuItemClasses = {
    root: classes.menuItem,
    selected: classes.selected,
  }

  return (
    <div>
      <FieldText
        aria-describedby={popId}
        className={cn(className, classes.fieldText)}
        defaultValue={defaultValue}
        disabled={disabled}
        label={label}
        onClick={!disabled && handleClick}
        {...rest}
        data-testid="timePicker"
      >
        {timeDisplay}
      </FieldText>
      <Popover
        classes={{ paper: classes.paper }}
        id={popId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuList classes={{ root: classes.list }}>
          {hours.map(hr => (
            <MenuItem
              autoFocus={hour === hr}
              classes={menuItemClasses}
              key={hr}
              onClick={() => handleSelect(hr, minute, period)}
              selected={hour === hr}
              value={hr}
              data-testid={`hour-${hr}`}
            >
              {hr}
            </MenuItem>
          ))}
        </MenuList>
        <MenuList classes={{ root: classes.list }}>
          {minutes.map(min => (
            <MenuItem
              autoFocus={minute === min}
              classes={menuItemClasses}
              key={min}
              onClick={() => handleSelect(hour, min, period)}
              selected={minute === min}
              value={min}
              data-testid={`minute-${min}`}
            >
              {min}
            </MenuItem>
          ))}
        </MenuList>
        <MenuList classes={{ root: classes.list }}>
          <MenuItem
            autoFocus={period === 'am'}
            classes={menuItemClasses}
            key="am"
            onClick={() => handleSelect(hour, minute, 'am')}
            selected={period === 'am'}
            value="am"
            data-testid="period-am"
          >
            am
          </MenuItem>
          <MenuItem
            autoFocus={period === 'pm'}
            classes={menuItemClasses}
            key="pm"
            onClick={() => handleSelect(hour, minute, 'pm')}
            selected={period === 'pm'}
            value="pm"
            data-testid="period-pm"
          >
            pm
          </MenuItem>
        </MenuList>
      </Popover>
      <input type="hidden" id={id} value={value} />
    </div>
  )
}

TimePicker.defaultProps = {
  increment: 1,
}

const StyledPicker = withStyles(styles)(TimePicker)
StyledPicker.displayName = 'TimePicker'
export default StyledPicker
